'use client'

import {
  BottomSheet,
  Button,
  Cell,
  Container,
  Dialog,
  Divider,
  InputTextArea,
  Navigation,
  Rating,
  SelectionGroup,
  Spacer,
  Text,
} from '@vinted/web-ui'
import { useCallback, useEffect, useRef, useState } from 'react'

import { X24 } from '@vinted/monochrome-icons'

import {
  submitSupportFeedback,
  chatbotFeedbackV2RequestClicked,
  autoreplyFeedbackV2RequestClicked,
  chatbotFeedbackV2FormSubmitted,
  autoReplyFeedbackV2FormSubmitted,
  chatbotFeedbackV2FormViewed,
  autoReplyFeedbackV2FormViewed,
  autoreplyFeedbackClosed,
  chatbotFeedbackClosed,
  autoreplyFeedbackV2RequestViewed,
  chatbotFeedbackV2RequestViewed,
} from '@marketplace-web/help-center-conversation-support/automated-support-feedback-data'

import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import useHelpCenterSessionTracking from '@marketplace-web/help-center/help-center-session-tracking-util'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'

import { FEEDBACK_REQUEST_MESSAGE_TYPES, getKeyPrefix } from './utils'

import {
  FeedbackRequestMessageProps,
  FeedbackRatingProps,
  FeedbackFormProps,
  FeedbackFormDialogProps,
} from './types'

const FeedbackRating = ({
  entityType,
  rating,
  handleRatingSelection,
  onRatingClick,
  ratingId = 'star-rating',
}: FeedbackRatingProps) => {
  const translate = useTranslate('')

  const keyPrefix = getKeyPrefix(entityType)
  const title = translate(`${keyPrefix}.title`)
  const subtitle = translate(`${keyPrefix}.subtitle`)
  const ariaLabel = translate('virtual_assistant_rating_form.title', {
    rating,
    max_rating: 5,
  })

  const handleSelect = (val: number) => {
    handleRatingSelection(val)
    onRatingClick(val)
  }

  return (
    <div className="request-message__header">
      <Text as="h2" text={title} type="section-heading" id="modal-title-id" />
      <Cell
        styling="narrow"
        body={<Text as="h2" type="title" text={subtitle} id="modal-subtitle-id" />}
      />
      <Spacer />
      <div className="u-flexbox u-justify-content-center u-align-items-center">
        <Rating
          aria={{
            'aria-label': ariaLabel,
          }}
          isInteractive
          name={ratingId}
          onSelect={handleSelect}
          value={rating}
          size="large"
          testId="feedback-request-star-rating"
        />
      </div>
      <Spacer size="large" />
    </div>
  )
}

const FeedbackForm = ({
  entityType,
  isIssueResolved,
  onIssueResolvedChange,
  inputRef,
  feedback,
  onFeedbackBlur,
  onSubmit,
  isSubmittingForm,
}: FeedbackFormProps) => {
  const translate = useTranslate('')

  const keyPrefix = getKeyPrefix(entityType)
  const questionText = translate(`${keyPrefix}.question`)
  const resolvedButtonText = translate(`${keyPrefix}.resolved_button`)
  const notResolvedButtonText = translate(`${keyPrefix}.not_resolved_button`)
  const followUpQuestionPlaceholder = translate(`${keyPrefix}.follow_up_question`)
  const submitButtonText = translate(`${keyPrefix}.submit_button`)
  const questionAriaLabelResolved = `${questionText}, ${resolvedButtonText}`
  const questionAriaLabelNotResolved = `${questionText}, ${notResolvedButtonText}`

  return (
    <Container paddingVertical="large">
      <Container paddingHorizontal="large">
        <Text as="h4" type="subtitle" text={questionText} id="feedback-question-id" />
      </Container>
      <div role="group" aria-labelledby="feedback-question-id">
        <SelectionGroup direction="horizontal" styling="default">
          <SelectionGroup.SelectionItem
            width="120px"
            isSelected={isIssueResolved === true}
            key="resolved"
            aria={{
              'aria-label': questionAriaLabelResolved,
            }}
            body={
              <div className="feedback-modal__button_text">
                <Text
                  theme="amplified"
                  as="p"
                  text={resolvedButtonText}
                  aria={{ 'aria-hidden': 'true' }}
                />
              </div>
            }
            showSelectionIndicator={false}
            onClick={() => onIssueResolvedChange(true)}
          />
          <SelectionGroup.SelectionItem
            showSelectionIndicator={false}
            width="120px"
            isSelected={isIssueResolved === false}
            key="not-resolved"
            aria={{
              'aria-label': questionAriaLabelNotResolved,
            }}
            onClick={() => onIssueResolvedChange(false)}
            body={
              <div className="feedback-modal__button_text">
                <Text
                  theme="amplified"
                  as="p"
                  text={notResolvedButtonText}
                  aria={{ 'aria-hidden': 'true' }}
                />
              </div>
            }
          />
        </SelectionGroup>
      </div>
      <Divider />
      <InputTextArea
        name="feedback-request-message"
        placeholder={followUpQuestionPlaceholder}
        aria={{ 'aria-label': followUpQuestionPlaceholder }}
        defaultValue={feedback}
        uncontrolled
        onBlur={onFeedbackBlur}
        ref={inputRef}
        testId="feedback-request-form"
      />
      <Spacer />
      <Container paddingHorizontal="large">
        <Button
          testId="fcr-form-submit-button"
          styling="filled"
          text={submitButtonText}
          theme="primary"
          onClick={onSubmit}
          isLoading={isSubmittingForm}
        />
      </Container>
    </Container>
  )
}

const FeedbackFormDialog = ({
  entityType,
  isVisible,
  handleFormDismiss,
  dialogAriaProps,
  ratingProps,
  formProps,
}: FeedbackFormDialogProps) => {
  const translate = useTranslate('')

  const keyPrefix = getKeyPrefix(entityType)
  const title = translate(`${keyPrefix}.title`)
  const subtitle = translate(`${keyPrefix}.subtitle`)
  const closeButtonAriaLabel = translate('common.a11y.actions.dialog_close')
  const dialogLabel = `${title}. ${subtitle}`

  return (
    <Dialog
      show={isVisible}
      enableCloseButton
      contentDimensions={{ width: 'max-content' }}
      aria={dialogAriaProps}
    >
      <span id="modal-dialog-label-id" className="u-visually-hidden">
        {dialogLabel}
      </span>
      <Navigation
        theme="transparent"
        right={
          <Button
            styling="flat"
            onClick={handleFormDismiss}
            iconName={X24}
            iconColor="greyscale-level-1"
            aria={{ 'aria-label': closeButtonAriaLabel }}
          />
        }
      />
      <FeedbackRating {...ratingProps} entityType={entityType} ratingId="star-rating-modal" />
      <Spacer size="medium" />
      <Divider />
      <FeedbackForm {...formProps} entityType={entityType} />
    </Dialog>
  )
}

const FeedbackRequestMessage = (props: FeedbackRequestMessageProps) => {
  const { entityType } = props

  const breakpoints = useBreakpoint()
  const translate = useTranslate('')
  const { track } = useTracking()
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const { conversation } = useConversationContext()
  const userId = useSession().user?.id
  const conversationId = conversation?.id
  const { helpCenterSessionId } = useHelpCenterSessionTracking()

  const [isExpandedFormVisible, setIsExpandedFormVisible] = useState<boolean>(false)
  const [isSubmittingForm, setIsSubmittingForm] = useState<boolean>(false)
  const [rating, setRating] = useState<number>(0)
  const [feedback, setFeedback] = useState<string>('')
  const [isIssueResolved, setIsIssueResolved] = useState<boolean | null>(null)

  useEffect(() => {
    if (!conversationId || !helpCenterSessionId) return
    const eventParams = {
      helpCenterSessionId,
      threadId: conversationId?.toString(),
    }
    if (entityType === FEEDBACK_REQUEST_MESSAGE_TYPES.AUTOREPLY) {
      track(autoreplyFeedbackV2RequestViewed(eventParams))
    } else {
      track(chatbotFeedbackV2RequestViewed(eventParams))
    }
  }, [conversationId, entityType, helpCenterSessionId, track])

  const handleSubmit = useCallback(() => {
    if (!userId || !conversationId || !helpCenterSessionId) return
    setIsSubmittingForm(true)

    const trackSubmitParams = {
      helpCenterSessionId,
      threadId: conversationId?.toString(),
      rating,
      ...(inputRef.current?.value !== '' && { comment: inputRef.current?.value }),
      ...(isIssueResolved !== null && { issueResolved: isIssueResolved }),
    }

    if (entityType === FEEDBACK_REQUEST_MESSAGE_TYPES.AUTOREPLY) {
      track(autoReplyFeedbackV2FormSubmitted(trackSubmitParams))
    } else {
      track(chatbotFeedbackV2FormSubmitted(trackSubmitParams))
    }

    submitSupportFeedback({ userId, conversationId }).finally(() => {
      setIsSubmittingForm(false)
      setIsExpandedFormVisible(false)
      setRating(0)
      setFeedback('')
      setIsIssueResolved(null)
      if (inputRef.current) {
        inputRef.current.value = ''
      }
    })
  }, [userId, conversationId, helpCenterSessionId, rating, isIssueResolved, entityType, track])

  const handleDismiss = useCallback(() => {
    if (!helpCenterSessionId || !conversationId) return
    const eventParams = {
      helpCenterSessionId,
      threadId: conversationId?.toString(),
      rating,
    }
    if (entityType === FEEDBACK_REQUEST_MESSAGE_TYPES.AUTOREPLY) {
      track(autoreplyFeedbackClosed(eventParams))
    } else {
      track(chatbotFeedbackClosed(eventParams))
    }
    setIsExpandedFormVisible(false)
    setRating(0)
    setFeedback('')
    setIsIssueResolved(null)
  }, [helpCenterSessionId, conversationId, rating, entityType, track])

  const trackRatingClick = (val: number) => {
    if (!conversationId || !helpCenterSessionId) return
    const eventParams = {
      helpCenterSessionId,
      threadId: conversationId?.toString(),
      rating: val,
    }
    if (entityType === FEEDBACK_REQUEST_MESSAGE_TYPES.AUTOREPLY) {
      track(autoreplyFeedbackV2RequestClicked(eventParams))
    } else {
      track(chatbotFeedbackV2RequestClicked(eventParams))
    }
  }

  const handleFormExpansion = (val: number) => {
    setRating(val)

    if (!conversationId || !helpCenterSessionId) return
    const eventParams = {
      helpCenterSessionId,
      threadId: conversationId?.toString(),
      rating: val,
    }
    if (entityType === FEEDBACK_REQUEST_MESSAGE_TYPES.AUTOREPLY) {
      track(autoReplyFeedbackV2FormViewed(eventParams))
    } else {
      track(chatbotFeedbackV2FormViewed(eventParams))
    }

    setIsExpandedFormVisible(true)
  }

  const closeButtonAriaLabel = translate('common.a11y.actions.dialog_close')

  return (
    <Container paddingHorizontal="regular">
      <Spacer size="x2-large" />
      <div aria-hidden={isExpandedFormVisible}>
        <FeedbackRating
          entityType={entityType}
          rating={rating}
          handleRatingSelection={handleFormExpansion}
          onRatingClick={trackRatingClick}
        />
      </div>
      {breakpoints.phones ? (
        <BottomSheet isVisible={isExpandedFormVisible} onClose={handleDismiss} initialHeight={1}>
          <Navigation
            theme="transparent"
            right={
              <Button
                styling="flat"
                onClick={handleDismiss}
                iconName={X24}
                iconColor="greyscale-level-1"
                aria={{ 'aria-label': closeButtonAriaLabel }}
              />
            }
          />
          <FeedbackRating
            entityType={entityType}
            rating={rating}
            handleRatingSelection={(val: number) => setRating(val)}
            onRatingClick={trackRatingClick}
            ratingId="star-rating-modal"
          />
          <Divider />
          <FeedbackForm
            entityType={entityType}
            isIssueResolved={isIssueResolved}
            onIssueResolvedChange={(value: boolean) => setIsIssueResolved(value)}
            inputRef={inputRef}
            feedback={feedback}
            onFeedbackBlur={() => setFeedback(inputRef.current?.value || '')}
            onSubmit={handleSubmit}
            isSubmittingForm={isSubmittingForm}
          />
        </BottomSheet>
      ) : (
        <FeedbackFormDialog
          entityType={entityType}
          isVisible={isExpandedFormVisible}
          handleFormDismiss={handleDismiss}
          dialogAriaProps={{ labelledby: 'modal-dialog-label-id' }}
          ratingProps={{
            rating,
            handleRatingSelection: (val: number) => setRating(val),
            onRatingClick: trackRatingClick,
          }}
          formProps={{
            isIssueResolved,
            onIssueResolvedChange: (value: boolean) => setIsIssueResolved(value),
            inputRef,
            feedback,
            onFeedbackBlur: () => setFeedback(inputRef.current?.value || ''),
            onSubmit: handleSubmit,
            isSubmittingForm,
          }}
        />
      )}
    </Container>
  )
}

export default FeedbackRequestMessage
